import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import { LIGHTBULB_URL, TIP_WIDTH } from "@/constants/tutorial";
import type { EmailLockTipProps, TooltipPos } from "@/interfaces/tutorial";
import { measureEmailLockTip } from "@/utils/tutorial-helper";
import { UpArrowNotch } from "./tip-arrows";

/**
 * The helper popover shown when a user clicks the LOCKED "Email Design" canvas tab in manual-send
 * mode. GfG isn't emailing — the sender distributes links themselves — so instead of switching to
 * the (inert) email canvas, this explains why and offers a way back: "Change Now" routes to Step 2's
 * Recipient Notification section where email can be re-enabled, "Nevermind"/X dismisses in place.
 *
 * Chrome mirrors `StartHereTip` (fixed popover, lightbulb, up-arrow, close); it's anchored below
 * `#tour-tab-email` via `measureEmailLockTip`. Ports the prototype's Phase 34.1 `.email-lock-tip`
 * (Campaign-Builder-August-2026-Revisions.html, `handleEmailTabClick` / `_positionEmailLockTip`).
 */
export default function EmailLockTip({ open, onClose, onChangeNow }: EmailLockTipProps) {
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<TooltipPos>(measureEmailLockTip);
  // `entered` drives the CSS transition — false = scaled/offset, true = in place.
  const [entered, setEntered] = useState(false);
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    if (open) {
      setPos(measureEmailLockTip());
      setMounted(true);
      // One rAF so the browser paints the "before" state before we flip entered=true.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntered(true));
      });
    } else {
      setEntered(false);
      unmountTimerRef.current = setTimeout(() => setMounted(false), 300);
    }
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
    };
  }, [open]);

  // Re-anchor on viewport resize so the tip tracks the tab.
  useEffect(() => {
    if (!mounted) return;
    function onResize() {
      setPos(measureEmailLockTip());
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mounted]);

  // Escape closes it, matching the other dismissible tips.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Email is off for this campaign"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: TIP_WIDTH,
        maxWidth: "calc(100vw - 32px)",
        background: "var(--color-white)",
        border: "1px solid var(--color-smoke)",
        borderRadius: 14,
        boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
        padding: "18px 20px",
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        lineHeight: 1.5,
        color: "var(--color-rodeo-black)",
        zIndex: 1000,
        transform: entered ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.96)",
        opacity: entered ? 1 : 0,
        transformOrigin: "top center",
        transition: entered
          ? "transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.22s ease-out"
          : "transform 0.2s ease-in, opacity 0.2s ease-in",
      }}
    >
      {/* Upward-pointing arrow aligned with the Email Design tab's center. */}
      <UpArrowNotch left={pos.arrowLeft} />

      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-1.5 right-2 flex items-center justify-center bg-transparent border-none text-graphite hover:text-rodeo-black hover:bg-clear-dust rounded-sm cursor-pointer p-1 transition-colors"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </button>

      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 72,
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={LIGHTBULB_URL}
          alt=""
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 12,
          minHeight: 72,
          paddingRight: 16,
        }}
      >
        <p style={{ margin: 0, color: "var(--color-rodeo-black)", fontSize: 14, lineHeight: 1.5 }}>
          If you want us to send your gift notification emails then change your Recipient
          Notification settings.
        </p>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={onChangeNow}
            className="inline-flex items-center bg-rodeo-black hover:bg-background-dark text-white border-none rounded-btn py-2 px-4 font-lato text-[11px] font-bold uppercase tracking-[1.5px] cursor-pointer transition-colors"
          >
            Change Now
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center bg-transparent text-graphite hover:text-rodeo-black border-none font-lato text-[12px] font-semibold cursor-pointer transition-colors"
          >
            Nevermind
          </button>
        </div>
      </div>
    </div>
  );
}
